/* Variant pills for the custom PDP variant_selector block.
 *
 * The <select> elements stay in the DOM as the source of truth: the block's inline script
 * (variant id, price, SKU) and simple-bundles-bridge.js (dependent option filtering) both
 * read and listen to them. This script only draws pills on top and forwards clicks as
 * `change` events on the matching <select>, so that existing wiring keeps working untouched.
 *
 * Per option group (thresholds come from the block settings):
 *   - desktop: always pills
 *   - more values than `pills-hide-over`: values that don't exist for the current selection are hidden
 *   - otherwise: every value is shown and unavailable ones are crossed out
 *   - mobile: groups with more values than `pills-dropdown-over` fall back to the <select> (CSS)
 */
(() => {
  const DEFAULT_HIDE_OVER = 8;
  const DEFAULT_DROPDOWN_OVER = 12;

  const toInt = (value, fallback) => {
    const n = parseInt(value, 10);
    return Number.isNaN(n) ? fallback : n;
  };

  function init(block) {
    const dataEl = block.querySelector('script[data-variant-selector-data]');
    const wrappers = [...block.querySelectorAll('.selector-wrapper[data-option-index]')];
    if (!dataEl || wrappers.length === 0) return;

    let variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (error) {
      console.warn('[variant-pills] Could not parse variant data:', error);
      return;
    }

    const hideOver = toInt(block.dataset.pillsHideOver, DEFAULT_HIDE_OVER);
    const dropdownOver = toInt(block.dataset.pillsDropdownOver, DEFAULT_DROPDOWN_OVER);

    const groups = wrappers.map((wrapper, index) => {
      const select = wrapper.querySelector('select');
      let values;
      try {
        values = JSON.parse(wrapper.dataset.values);
      } catch (error) {
        values = [...select.options].map((option) => option.value);
      }

      const container = document.createElement('div');
      container.className = 'vp-pills';
      container.setAttribute('role', 'radiogroup');
      container.setAttribute('aria-labelledby', `SingleOptionLabel-${index}`);

      const pills = values.map((value) => {
        const pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'vp-pill';
        pill.setAttribute('role', 'radio');
        pill.dataset.value = value;
        pill.textContent = value;
        container.append(pill);
        return pill;
      });

      select.after(container);
      wrapper.classList.add('vp-enhanced');
      if (values.length > dropdownOver) wrapper.dataset.dropdown = 'true';

      return { index, wrapper, select, values, container, pills };
    });

    const selection = () => groups.map((group) => group.select.value);

    // Variants matching the options chosen *before* this group (the same cascade the bridge
    // uses) and this group's candidate value.
    const matching = (group, value, selected) =>
      variants.filter(
        (variant) =>
          variant.options[group.index] === value &&
          selected.slice(0, group.index).every((chosen, i) => variant.options[i] === chosen)
      );

    function render() {
      const selected = selection();

      groups.forEach((group) => {
        const hideInvalid = group.values.length > hideOver;
        let firstVisible = null;
        let checkedPill = null;

        group.pills.forEach((pill) => {
          const value = pill.dataset.value;
          const candidates = matching(group, value, selected);
          const exists = candidates.length > 0;
          const soldOut = exists && !candidates.some((variant) => variant.available);
          const checked = value === selected[group.index];

          pill.hidden = hideInvalid && !exists;
          pill.disabled = !exists;
          pill.classList.toggle('is-unavailable', !exists || soldOut);
          pill.setAttribute('aria-checked', String(checked));
          pill.tabIndex = -1;

          if (!pill.hidden && !firstVisible) firstVisible = pill;
          if (checked && !pill.hidden) checkedPill = pill;
        });

        (checkedPill || firstVisible)?.setAttribute('tabindex', '0');
      });
    }

    function choose(group, pill) {
      if (pill.disabled || group.select.value === pill.dataset.value) return;

      group.select.value = pill.dataset.value;
      if (group.select.value !== pill.dataset.value) {
        // The bridge rebuilds the <option> list; make sure the value is selectable.
        group.select.add(new Option(pill.dataset.value, pill.dataset.value, false, true));
      }
      group.select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    groups.forEach((group) => {
      group.container.addEventListener('click', (event) => {
        const pill = event.target.closest('.vp-pill');
        if (pill) choose(group, pill);
      });

      group.container.addEventListener('keydown', (event) => {
        const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
        if (!step) return;

        const usable = group.pills.filter((pill) => !pill.hidden && !pill.disabled);
        const current = usable.indexOf(document.activeElement);
        if (current === -1) return;

        event.preventDefault();
        const next = usable[(current + step + usable.length) % usable.length];
        next.focus();
        choose(group, next);
      });
    });

    // Bubbles up after the bridge (document, capture) and the block's own handler have run,
    // so the selects already hold their final values here.
    block.addEventListener('change', render);

    // The bridge rebuilds <option> lists on its own during init.
    const observer = new MutationObserver(render);
    groups.forEach((group) => observer.observe(group.select, { childList: true }));

    render();
  }

  const start = () => document.querySelectorAll('.product-variant-selector').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
